/**
 * One clock for every freshness chip on the page (ANL-E04-S09, Codex P2 on #3605). A chip is judged
 * against the time, so on an open page it must age from Fresh to Delayed to Stale without a refetch.
 * One interval serves every subscriber; it starts with the first and stops with the last, so a page
 * with no chip runs no timer. Pure TypeScript: the React side is `useFreshnessNow`.
 */

// Chip labels change at minute granularity ("Delayed 42 min", "as of 14:06 UTC").
export const FRESHNESS_TICK_MS = 60_000;

export type ClockTimers = {
  setInterval: (tick: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
  now: () => number;
};

export type FreshnessClock = {
  /** The time of the latest tick (or of the first subscription); stable between ticks. */
  now: () => number;
  /** Calls `listener` on every tick; returns the unsubscribe. */
  subscribe: (listener: () => void) => () => void;
};

const SYSTEM_TIMERS: ClockTimers = {
  setInterval: (tick, ms) => setInterval(tick, ms),
  clearInterval: (handle) => clearInterval(handle as ReturnType<typeof setInterval>),
  now: () => Date.now(),
};

export const createFreshnessClock = (
  timers: ClockTimers = SYSTEM_TIMERS,
  tickMs = FRESHNESS_TICK_MS,
): FreshnessClock => {
  const listeners = new Set<() => void>();
  let now = timers.now();
  let handle: unknown = null;

  const tick = () => {
    now = timers.now();
    for (const listener of listeners) listener();
  };

  return {
    now: () => now,
    subscribe: (listener) => {
      listeners.add(listener);
      if (handle === null) {
        // The page may have sat idle since the last tick: read the time afresh.
        now = timers.now();
        handle = timers.setInterval(tick, tickMs);
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && handle !== null) {
          timers.clearInterval(handle);
          handle = null;
        }
      };
    },
  };
};

/** The page's clock: every chip shares it, so the page runs at most one interval. */
export const freshnessClock = createFreshnessClock();
